import axiosInstance from "@/api/axiosInstance";
import {
  SpotImage,
  SpotImagesResponse,
  UpdateImageOrderRequest,
  SetCoverRequest,
  SpotSearchResult,
} from "@/common/model/spotimages/SpotImage";
import { requestHandler } from "@/helpers/requestHandler";

const BASE_PATH = "/spotimages";

export const SpotImageService = {
  /**
   * Search for spots by name (uses existing kiteworldmap endpoint)
   */
  async searchSpots(query: string, limit: number = 20): Promise<SpotSearchResult[]> {
    return requestHandler<SpotSearchResult[]>(async () => {
      const response = await axiosInstance.get("/kiteworldmap/spots/query", {
        params: { filter: query, limit },
      });
      // Map the response to SpotSearchResult format. The /kiteworldmap/spots/query
      // endpoint returns coordinates as `lat`/`lon` and the country as `country`/
      // `countryid` (lowercase) — NOT latitude/longitude/countryName. Reading the wrong
      // keys left latitude/longitude undefined, which surfaced in TestData inject as
      // "coordinates unavailable" for spots that do have coordinates. Fall back to the
      // camelCase names too, in case a sibling endpoint ever returns those.
      return response.data.map((spot: any) => ({
        id: spot.id,
        name: spot.name,
        latitude: spot.lat ?? spot.latitude,
        longitude: spot.lon ?? spot.longitude,
        countryId: spot.countryid ?? spot.countryId,
        countryName: spot.country ?? spot.countryName,
        alpha2: spot.alpha2,
      }));
    }, "SpotImageService -> searchSpots");
  },

  /**
   * Get all images for a spot (selected + available)
   */
  async getImages(spotId: number): Promise<SpotImagesResponse> {
    return requestHandler<SpotImagesResponse>(async () => {
      const response = await axiosInstance.get(`${BASE_PATH}/${spotId}/images`);
      return response.data;
    }, "SpotImageService -> getImages");
  },

  /**
   * Update the order of selected images
   */
  async updateOrder(spotId: number, orderedFilenames: string[]): Promise<void> {
    return requestHandler<void>(async () => {
      const request: UpdateImageOrderRequest = { orderedFilenames };
      await axiosInstance.put(`${BASE_PATH}/${spotId}/images/order`, request);
    }, "SpotImageService -> updateOrder");
  },

  /**
   * Set a new cover image (must be from selected images)
   */
  async setCover(spotId: number, filename: string): Promise<void> {
    return requestHandler<void>(async () => {
      const request: SetCoverRequest = { filename };
      await axiosInstance.put(`${BASE_PATH}/${spotId}/images/cover`, request);
    }, "SpotImageService -> setCover");
  },

  /**
   * Add an image from available pool to selected (content.json)
   */
  async addToSelection(spotId: number, filename: string): Promise<void> {
    return requestHandler<void>(async () => {
      await axiosInstance.post(`${BASE_PATH}/${spotId}/images/${filename}/add`);
    }, "SpotImageService -> addToSelection");
  },

  /**
   * Remove an image from selection (moves to available, file stays in folder)
   */
  async removeFromSelection(spotId: number, filename: string): Promise<void> {
    return requestHandler<void>(async () => {
      await axiosInstance.delete(`${BASE_PATH}/${spotId}/images/${filename}`);
    }, "SpotImageService -> removeFromSelection");
  },

  /**
   * Permanently delete an image from GCS folder
   */
  async deletePermanently(spotId: number, filename: string): Promise<void> {
    return requestHandler<void>(async () => {
      await axiosInstance.delete(`${BASE_PATH}/${spotId}/images/${filename}/permanent`);
    }, "SpotImageService -> deletePermanently");
  },

  /**
   * Upload a new image
   */
  async uploadImage(spotId: number, file: File, addToSelection: boolean = false): Promise<SpotImage> {
    return requestHandler<SpotImage>(async () => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("addToSelection", String(addToSelection));

      const response = await axiosInstance.post(
        `${BASE_PATH}/${spotId}/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    }, "SpotImageService -> uploadImage");
  },

  /**
   * Reset to ML rankings (remove manual override)
   */
  async resetToMlRankings(spotId: number): Promise<void> {
    return requestHandler<void>(async () => {
      await axiosInstance.post(`${BASE_PATH}/${spotId}/images/reset`);
    }, "SpotImageService -> resetToMlRankings");
  },
};
